import { useGetBuyerSpendingLimit } from '@api/lendingAPI/lapiHooks';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { tracking } from '@iwoca/frontend-tracking-library';

import { RefreshExpiredLimits } from './RefreshExpiredLimits';
import { SpendingLimitBar } from './SpendingLimitBar';
import { daysSinceStatusDate } from '../../Dashboard.helpers';
import { Application } from '../../Dashboard.types';
import styles from '../SpendingLimitCard.module.css';
import {
    checkHasThirtyDayAndThreeMonthLimit,
    formatDuration
} from './formatDuration';

export const DeclinedLimits = ({
    setIsSpendingLimitDrawerOpen,
    refreshSpendingLimit,
    loading,
    application
}: {
    setIsSpendingLimitDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
    refreshSpendingLimit: () => void;
    loading: boolean;
    application: Application;
}) => {
    const { buyerSpendingLimit, loadingBuyerSpendingLimit } =
        useGetBuyerSpendingLimit();
    const spendingLimit = buyerSpendingLimit?.active_spending_limit;
    // Ensure the group formatting only happens when customer is active for the thirty day limit
    const hasThirtyDayAndThreeMonthLimit = checkHasThirtyDayAndThreeMonthLimit(
        buyerSpendingLimit?.spending_limits
    );

    if (loadingBuyerSpendingLimit || !spendingLimit) return null;

    const daysSinceDecline = daysSinceStatusDate(application);

    // Don't let them refresh until 30 days after decline.
    // If we don't know how many days since decline, let them try
    const showRefreshButton =
        daysSinceDecline !== null ? daysSinceDecline >= 30 : true;
    return (
        <div className={styles.limits}>
            <div className={styles.left}>
                <div className={styles.remainingLimitHeader}>
                    You have{' '}
                    <FormattedCurrency
                        className={styles.remainingLimitAmount}
                        value={0}
                    />{' '}
                    left to spend
                </div>
                <div className={styles.interestRateLabel}>
                    We can’t approve you for more funds right now - check back
                    in 30 days and hit ‘Secure my limit’.
                </div>
            </div>
            <div className={styles.right}>
                <div className={styles.spendingLimitAmount}>
                    <div>
                        Spending limit: <FormattedCurrency value={0} />
                    </div>
                    {showRefreshButton && (
                        <RefreshExpiredLimits
                            loading={loading}
                            setIsSpendingLimitDrawerOpen={
                                setIsSpendingLimitDrawerOpen
                            }
                            refreshSpendingLimit={refreshSpendingLimit}
                        />
                    )}
                </div>
                <div className={styles.spendingLimitDurationLabel}>
                    {formatDuration({
                        duration: spendingLimit.duration_in_months,
                        groupSameLimits: hasThirtyDayAndThreeMonthLimit
                    })}{' '}
                    purchases{' '}
                    <button
                        onClick={() => {
                            tracking.ampli.spendingLimitViewedOtherTerms();
                            setIsSpendingLimitDrawerOpen(true);
                        }}
                        className={styles.otherTermsButton}
                    >
                        Other terms
                    </button>
                </div>
            </div>
            <div className={styles.bar}>
                <SpendingLimitBar status="declined" />
            </div>
        </div>
    );
};
